import {
  DragEvent as SyntheticDragEvent,
  useEffect,
  useState,
  useRef,
  ChangeEvent
} from 'react'
import { UploadIcon } from '@heroicons/react/outline'
import { CheckCircleIcon } from '@heroicons/react/solid'
import { ErrorMessage, useFormikContext } from 'formik'

import type { FormValues } from '.'
import { InvalidMessage } from '@components/UI/Form/InvalidMessage'

export function UploadInspection() {
  const fileInput = useRef<HTMLInputElement>(null)
  const { values, errors, touched, setFieldValue } =
    useFormikContext<FormValues>()
  const filename = values.inspection?.name ?? ''
  const [isDragHovered, setIsDragHovered] = useState(false)

  useEffect(() => {
    function preventDefault(e: DragEvent) {
      e.preventDefault()
    }

    window.addEventListener('dragover', preventDefault)

    return () => {
      window.removeEventListener('dragover', preventDefault)
    }
  })

  function onDragOver() {
    setIsDragHovered(true)
  }

  function onDragLeave() {
    setIsDragHovered(false)
  }

  function onDrop(event: SyntheticDragEvent<HTMLLabelElement>) {
    event.preventDefault()
    event.stopPropagation()

    setIsDragHovered(false)

    selectInspection(event.dataTransfer.files?.[0])
  }

  function selectInspection(file?: File) {
    if (!file) return
    void setFieldValue('inspectionKey', '', false)
    void setFieldValue('inspection', file)
  }

  return (
    <div className="block bg-white p-8 rounded shadow-sm" id="inspection">
      <h2 className="text-2xl font-medium mb-8">
        Upload single family home inspection
      </h2>
      <label
        className={`rounded border-dashed border-4 border-gray-300 text-gray-300 w-full h-48 flex flex-col justify-center items-center cursor-pointer transition-colors ${
          isDragHovered
            ? 'bg-blue-100'
            : errors.inspection && touched.inspection
            ? 'bg-red-50 border-red-200 text-red-200'
            : 'bg-white'
        }`}
        htmlFor="inspection-pdf"
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        <input
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            if (event.currentTarget.files?.[0]) {
              selectInspection(event.currentTarget.files?.[0])
            }
          }}
          ref={fileInput}
          className="hidden"
          type="file"
          name="inspection"
          id="inspection-pdf"
          accept=".pdf"
        />
        {filename ? (
          <div className="relative rounded-full bg-green-200 text-green-600 p-2 font-bold mb-2 flex max-w-full">
            <CheckCircleIcon width={20} height={20} className="z-10" />
            <span className="mx-2 z-10 min-w-0 break-all">{filename}</span>
          </div>
        ) : (
          <UploadIcon width={48} height={48} className="mb-4" />
        )}
        <p className="text-center text-gray-700">
          {filename.length === 0 && 'Drop your inspection PDF here or '}
          <label
            htmlFor="inspection-pdf"
            className={`hover:underline cursor-pointer ${
              filename.length > 0 ? 'text-gray-400' : 'text-brand'
            }`}
          >
            {filename.length > 0 ? 'Select a Different File' : 'browse'}
          </label>
        </p>
        <ErrorMessage
          component={InvalidMessage}
          name="inspection"
          className="text-red-500"
        />
      </label>
    </div>
  )
}
